import type {
  AiBookingDraft,
  BookingBooker,
  BookingBookerAffiliate,
  BookingBookerCustomer,
  BookingPrefill,
  BookingServiceType,
  BookingVehicleCapacity,
} from '../../../api/comms';
import type { PlaceDetail, VehicleTypeItem } from '../../../api/dispatch';
import type { CrmContact } from '../../../api/types';
import { airportCodeIn, stripLegLabel, stripVehicleLabel } from '../../../lib/bookingShorthand';

/**
 * The editable booking form's state, seeded from the AI's free-text draft and
 * the thread's contact. Numeric inputs stay strings until payload build — the
 * form must tolerate half-typed values without fighting the user.
 */

export interface LegState {
  /** What the field shows — the AI's raw text until a place resolves. */
  text: string;
  resolving: boolean;
  resolved: PlaceDetail | null;
  /** The suggestion label that matched, for the resolved-address hint line. */
  matchedText: string | null;
  error: string | null;
}

/**
 * How the job gets paid, as the agent chooses it. `billLater` is dispatch's
 * `isBilling: true` — the ONLY way it accepts a non-quote job with no payment
 * method, and therefore what both "Skip" and "Book anyway" send.
 */
export type PaymentMode = 'card' | 'paymentLink' | 'billLater';

/**
 * Who is PLACING the job, which dispatch keeps separate from who is driven
 * (`BookerSection` in the dashboard's Add Job, same three values). One
 * `accountNumber` reaches the wire as `customerId` or `affiliateId` depending
 * on which of these it is; a guest sends neither and only the booked* fields.
 */
export type BookerType = 'guest' | 'customer' | 'affiliate';

/**
 * Why the form holds the booker it holds, which is what the chip says about it.
 * `match` is the sender's address meeting a dispatch record — an unverified
 * customer match is a guess and is tinted as one. `remembered` is an agent's
 * earlier answer for this address; `picked` is this agent, just now.
 */
export type BookerSource = 'match' | 'remembered' | 'picked';

export interface BookingFormState {
  serviceType: BookingServiceType;
  durationHours: string;
  pickup: LegState;
  dropoff: LegState;
  stops: LegState[];
  /** Hourly only — mirrors the dashboard's "same as pickup" checkbox. */
  dropoffSameAsPickup: boolean;
  /** datetime-local value, YYYY-MM-DDTHH:mm — wall time in the pickup's zone. */
  pickupDatetime: string;
  /** IANA zone resolved from the pickup coordinates; null = browser-zone fallback. */
  timeZoneId: string | null;
  passengerFirstName: string;
  passengerLastName: string;
  passengerPhone: string;
  passengerEmail: string;
  passengerCount: string;
  luggageCount: string;
  /** 0 = not chosen — same "unset" convention as the dashboard. */
  vehicleTypeId: number;
  /** The AI's free-text wish, kept for the "AI suggested" hint. */
  vehiclePreference: string;
  flightNumber: string;
  airline: string;
  customerNote: string;
  chauffeurNote: string;
  /** The contact's dispatch link, if any — an UNVERIFIED link is an email guess. */
  dispatchCustomerId: number | null;
  dispatchLinkVerified: boolean;
  bookerType: BookerType;
  bookerSource: BookerSource;
  /**
   * The matched rows, kept whole rather than as ids: a parked draft has to
   * repaint the booker's name in Add Job without a lookup, and the rows come
   * from the sender's mailbox or from the picker's search — neither of which
   * the payload builders can repeat.
   */
  bookerAffiliate: BookingBookerAffiliate | null;
  bookerCustomer: BookingBookerCustomer | null;
  /**
   * The guest booker's identity, and the ONLY booker fields that are free text.
   * Deliberately real state rather than derived from the passenger: on a farm-in
   * they are different people, and deriving them is what put the passenger's
   * name on the wire as the booker of an affiliate's job.
   */
  bookerName: string;
  bookerEmail: string;
  bookerPhone: string;
  /** The booker's own reservation number — dispatch's `requesterResNo`. */
  bookerReference: string;
  paymentMode: PaymentMode;
  /** Stripe `pm_…` id — only read when `paymentMode` is `card`. */
  paymentMethodId: string;
  sendNotifications: boolean;
  returnTripRequested: boolean;
  summary: string;
}

export const emptyLeg = (text = ''): LegState => ({
  text,
  resolving: false,
  resolved: null,
  matchedText: null,
  error: null,
});

const splitName = (name: string | null): { first: string; last: string } => {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return { first: '', last: '' };
  const space = trimmed.indexOf(' ');
  if (space === -1) return { first: trimmed, last: '' };
  return { first: trimmed.slice(0, space), last: trimmed.slice(space + 1) };
};

/**
 * Which booker the form opens on. The service already decided (`suggested`),
 * and an affiliate wins there because it is a trading relationship somebody set
 * up deliberately — but the choice is only offered when the row it needs is
 * actually present, so a service that sent a suggestion without its row falls
 * back to the customer link and then to guest rather than to an empty tab.
 */
const seedBookerType = (
  booker: BookingBooker | null | undefined,
  contact: CrmContact | null,
): BookerType => {
  if (booker?.suggested === 'affiliate' && booker.affiliate) return 'affiliate';
  if (booker?.customer || contact?.dispatchCustomerId) return 'customer';
  return 'guest';
};

/**
 * Both switches on — an older service that sends no `prefill`, and every
 * default this module reads when a caller passes none.
 */
export const PREFILL_ON: BookingPrefill = {
  paxInfo: true,
  paxLugFromVehicle: true,
  vehicleCapacities: [],
};

/**
 * The account this form will book on, once `seedBookerType` has chosen — the
 * only contact details a passenger may be backstopped from, and the same
 * account the dashboard's Add Job fills from when a dispatcher picks one.
 * A guest booking has no account, and so fills nothing.
 */
const bookerAccount = (
  type: BookerType,
  affiliate: BookingBookerAffiliate | null,
  customer: BookingBookerCustomer | null,
): { phone: string | null; email: string | null } | null => {
  if (type === 'affiliate') return affiliate ? { phone: affiliate.phone, email: affiliate.email } : null;
  if (type === 'customer') return customer ? { phone: customer.phone, email: customer.email } : null;
  return null;
};

export function seedFromDraft(
  draft: AiBookingDraft,
  contact: CrmContact | null,
  booker: BookingBooker | null = null,
  prefill: BookingPrefill = PREFILL_ON,
): BookingFormState {
  // The AI only fills passenger fields it saw in the thread; the contact row
  // backstops them so a known customer never arrives as a blank passenger.
  // The name backstop is all-or-nothing: mixing an AI first name with the
  // contact's last name would fabricate a person ("Ava" + "Webb" = "Ava Webb").
  const contactName = splitName(contact?.name ?? null);
  const aiNamedPassenger =
    draft.passengerFirstName !== undefined || draft.passengerLastName !== undefined;
  const first = aiNamedPassenger ? (draft.passengerFirstName ?? '') : contactName.first;
  const last = aiNamedPassenger ? (draft.passengerLastName ?? '') : contactName.last;
  const bookerType = seedBookerType(booker, contact);
  const seededAffiliate = booker?.affiliate ?? null;
  // The link on the contact is the customer the form already booked on; the
  // service's own match only fills in when there is no link to disagree with.
  const seededCustomer =
    booker?.customer ??
    (contact?.dispatchCustomerId
      ? {
          id: contact.dispatchCustomerId,
          name: contact.name ?? null,
          email: contact.email ?? null,
          phone: contact.phoneNumber ?? null,
        }
      : null);
  // What the thread said always wins. Below it, the tenant's `paxInfo` switch
  // decides whether anything is filled at all: off, a passenger the AI could
  // not find stays blank and the agent types it, which is precisely what the
  // dashboard does with the same switch off. On, the sender comes first (they
  // wrote in, so their number is the one that reached us) and the account
  // behind the booking second — an affiliate's company line is better than an
  // empty required field, and worse than a human who just emailed.
  const account = prefill.paxInfo ? bookerAccount(bookerType, seededAffiliate, seededCustomer) : null;
  const backstop = (value: string | null | undefined, accountValue: string | null | undefined) =>
    prefill.paxInfo ? (value ?? accountValue ?? '') : '';
  const phone = draft.passengerPhone ?? backstop(contact?.phoneNumber, account?.phone);
  const email = draft.passengerEmail ?? backstop(contact?.email, account?.email);
  return {
    serviceType: draft.serviceType === 'hourly' ? 'hourly' : 'oneway',
    durationHours: draft.durationHours != null ? String(draft.durationHours) : '',
    // "PU: JFK T4" is the shape a reservation mail hands over; the label is
    // dropped here rather than at search time so the field shows the agent the
    // same address the lookup was given.
    pickup: emptyLeg(stripLegLabel(draft.pickupAddressText ?? '')),
    dropoff: emptyLeg(stripLegLabel(draft.dropoffAddressText ?? '')),
    stops: (draft.stops ?? []).map((s) => emptyLeg(stripLegLabel(s))),
    dropoffSameAsPickup: draft.serviceType === 'hourly' && !draft.dropoffAddressText,
    pickupDatetime: draft.pickupDatetime ?? '',
    timeZoneId: null,
    passengerFirstName: first,
    passengerLastName: last,
    passengerPhone: phone,
    passengerEmail: email,
    passengerCount: draft.passengerCount != null ? String(draft.passengerCount) : '',
    luggageCount: draft.luggageCount != null ? String(draft.luggageCount) : '',
    vehicleTypeId: 0,
    vehiclePreference: stripVehicleLabel(draft.vehiclePreference ?? ''),
    flightNumber: draft.flightNumber ?? '',
    airline: draft.airline ?? '',
    customerNote: draft.customerNote ?? '',
    chauffeurNote: draft.chauffeurNote ?? '',
    dispatchCustomerId: contact?.dispatchCustomerId ?? null,
    dispatchLinkVerified: contact?.dispatchLinkVerified ?? false,
    bookerType,
    bookerSource: booker?.remembered ? 'remembered' : 'match',
    bookerAffiliate: seededAffiliate,
    bookerCustomer: seededCustomer,
    // The SENDER, not the passenger — that is the whole point of the split.
    bookerName: contact?.name ?? '',
    bookerEmail: contact?.email ?? '',
    bookerPhone: contact?.phoneNumber ?? '',
    bookerReference: draft.bookerReference ?? '',
    // Seeded to the one choice that is always valid. The box upgrades this to
    // the customer's first saved card once the lookup lands — while it is in
    // flight the radios are not rendered, so that can never clobber a pick.
    paymentMode: 'billLater',
    paymentMethodId: '',
    // Off by default (the dashboard defaults on): a booking drafted out of a
    // chat should email the customer only when the agent opts in.
    sendNotifications: false,
    returnTripRequested: draft.returnTripRequested === true,
    summary: draft.summary ?? '',
  };
}

/** A booker the agent chose in the picker — a customer or an affiliate, never a guest. */
export type BookerPick =
  | { type: 'customer'; customer: BookingBookerCustomer }
  | { type: 'affiliate'; affiliate: BookingBookerAffiliate };

/**
 * The form after an agent picks a booker the draft did not find.
 *
 * Only the booker shape is kept: a search result also carries why it matched
 * and which company it belongs to, which the payload builders must never see —
 * a stray field there is a field dispatch is sent.
 *
 * `remembered` says the pick was also written onto the contact, so the form's
 * copy of the contact's link moves with it. Verified goes false on a different
 * customer, because the server resets it the same way: a challenge proved this
 * channel owns ONE account, and a re-link is not that account.
 *
 * A selected card belongs to the customer that was named, so it is dropped
 * whenever the paying customer changes — the same rule as switching tabs.
 */
export function applyBookerPick(
  state: BookingFormState,
  pick: BookerPick,
  { remembered }: { remembered: boolean },
): BookingFormState {
  const next: BookingFormState =
    pick.type === 'customer'
      ? {
          ...state,
          bookerType: 'customer',
          bookerSource: 'picked',
          bookerCustomer: {
            id: pick.customer.id,
            name: pick.customer.name,
            email: pick.customer.email,
            phone: pick.customer.phone,
          },
        }
      : {
          ...state,
          bookerType: 'affiliate',
          bookerSource: 'picked',
          bookerAffiliate: {
            id: pick.affiliate.id,
            companyName: pick.affiliate.companyName,
            email: pick.affiliate.email,
            phone: pick.affiliate.phone,
            primaryContactName: pick.affiliate.primaryContactName,
            primaryContactEmail: pick.affiliate.primaryContactEmail,
            primaryContactPhone: pick.affiliate.primaryContactPhone,
            matchedEmail: pick.affiliate.matchedEmail,
          },
        };
  if (pick.type === 'customer' && remembered) {
    next.dispatchCustomerId = pick.customer.id;
    next.dispatchLinkVerified = state.dispatchCustomerId === pick.customer.id && state.dispatchLinkVerified;
  }
  const payer = (form: BookingFormState) =>
    form.bookerType === 'customer' ? (form.bookerCustomer?.id ?? null) : null;
  if (state.paymentMode === 'card' && payer(state) !== payer(next)) {
    return { ...next, paymentMode: 'billLater', paymentMethodId: '' };
  }
  return next;
}

/**
 * Whether the customer on the form is the one the CONTACT is verified as.
 * The flag is the contact's, so it says nothing about a customer picked for
 * this booking alone.
 */
export const bookerVerified = (form: BookingFormState): boolean =>
  form.bookerType === 'customer' &&
  !!form.bookerCustomer &&
  form.dispatchLinkVerified &&
  form.bookerCustomer.id === form.dispatchCustomerId;

/**
 * The pickup's IATA code, read off the place dispatch resolved rather than
 * stored — nothing can then hold a code for an address the agent has since
 * corrected.
 *
 * It is not cosmetic. Dispatch filters the flight-number lookup by it
 * (`utils/findbestflight.js`), so an empty code lets it attach whichever leg of
 * DL204 is nearest in time instead of the one landing where the chauffeur
 * waits. A WRONG code filters every flight out, which is why this only ever
 * reads a place dispatch itself typed as an airport.
 */
export const pickupAirportCode = (pickup: LegState): string =>
  pickup.resolved?.addressType === 'A'
    ? airportCodeIn(pickup.matchedText, pickup.resolved.addressLine1, pickup.text)
    : '';

/** The collapsible cards the editing form is grouped into, in render order. */
export type BookingSectionId = 'trip' | 'passenger' | 'booker' | 'vehicle' | 'notes' | 'payment';

export type MissingKey =
  | 'firstName'
  | 'lastName'
  | 'phone'
  | 'email'
  | 'pax'
  | 'luggage'
  | 'vehicle'
  | 'pickupTime'
  | 'pickupAddress'
  | 'dropoffAddress'
  | 'duration'
  | 'card';

export interface MissingItem {
  /** Stable id — drives the per-field highlight after a blocked save. */
  key: MissingKey;
  /** Human wording for the footer tooltip and the "still needed" callout. */
  label: string;
  /** Which card holds the field, so the callout can open the right one. */
  section: BookingSectionId;
}

export interface SaveValidation {
  ok: boolean;
  /** What still blocks a full job save, in dispatch's own checking order. */
  missing: MissingItem[];
}

// Deliberately loose: this catches "sarah@" and "sarah at acme.com", not every
// RFC oddity. A stricter pattern rejects addresses dispatch happily accepts.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Shared with both payload builders — blank or nonsense is null. */
export const parseCount = (value: string): number | null => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
};

/**
 * Luggage is the one count where 0 is an ANSWER, not a blank — "no bags" has
 * to survive to the wire as 0, or a required field the agent filled in reaches
 * dispatch empty and the check below bought nothing.
 */
export const parseLuggageCount = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
};

/**
 * Everything dispatch-be needs before it will create a real job, checked here
 * so the footer can name what is missing instead of the agent discovering it
 * through a 400 on a request that is NOT safe to retry.
 *
 * The list is `commonApis/jobcreate.js`, in its order: a resolvable
 * `vehicleTypeId`, a `pickUpTimeDate` on the pickup leg, addresses with real
 * coordinates (the price calculation routes between them), a primary passenger
 * with name/phone/email — the empty `{}` is skipped outright, and the email
 * doubles as the customer lookup key — and, for a non-quote, a payment method.
 *
 * The passenger and luggage counts are the one addition to that list: dispatch
 * stores them null without complaint, and they are demanded here anyway.
 */
export function validateForSave(
  form: BookingFormState,
  options: { isQuote?: boolean } = {},
): SaveValidation {
  const missing: MissingItem[] = [];
  const need = (key: MissingKey, label: string, section: BookingSectionId) =>
    missing.push({ key, label, section });
  if (!form.passengerFirstName.trim()) need('firstName', 'passenger first name', 'passenger');
  if (!form.passengerLastName.trim()) need('lastName', 'passenger last name', 'passenger');
  if (!form.passengerPhone.trim()) need('phone', 'passenger phone', 'passenger');
  const email = form.passengerEmail.trim();
  // Not cosmetic: dispatch matches this against its customer table and stores
  // it as bookedEmail, so a typo silently books a job nobody can be reached on.
  if (!email) need('email', 'passenger email', 'passenger');
  else if (!EMAIL_RE.test(email)) need('email', 'a valid passenger email', 'passenger');
  // Not dispatch's rule but ours: the job is dispatched on these two. They pick
  // the vehicle and its boot space, and a party of six must never reach a
  // chauffeur as a blank. `countsFromVehicle` fills them from the chosen
  // vehicle the way the dashboard does, but only when the tenant's switch is on
  // and only into an empty box — so the check stays, and answers for the agent
  // who turned the filling off or cleared a count on purpose.
  if (parseCount(form.passengerCount) === null) need('pax', 'passenger count', 'passenger');
  if (parseLuggageCount(form.luggageCount) === null) need('luggage', 'luggage count', 'passenger');
  if (!form.vehicleTypeId) need('vehicle', 'vehicle', 'vehicle');
  if (!form.pickupDatetime) need('pickupTime', 'pickup time', 'trip');
  if (!form.pickup.resolved) need('pickupAddress', 'pickup address', 'trip');
  const needsDropoff = form.serviceType === 'oneway' || !form.dropoffSameAsPickup;
  if (needsDropoff && !form.dropoff.resolved) need('dropoffAddress', 'dropoff address', 'trip');
  if (form.serviceType === 'hourly' && !(Number(form.durationHours) > 0)) {
    need('duration', 'duration', 'trip');
  }
  // A quote is exempt — dispatch nulls payment for one. For a job, only the
  // card branch can be incomplete; the other two carry no id to forget.
  if (!options.isQuote && form.paymentMode === 'card' && !form.paymentMethodId.trim()) {
    need('card', 'a card to charge', 'payment');
  }
  return { ok: missing.length === 0, missing };
}

/** The `missing` items that live in one card, for its badge and highlight. */
export const missingInSection = (
  validation: SaveValidation,
  section: BookingSectionId,
): MissingItem[] => validation.missing.filter((m) => m.section === section);

const tokens = (value: string) =>
  value
    .toLowerCase()
    .split(/\W+/)
    .filter((t) => t.length > 1);

/**
 * The vehicle list as the box should show it: dispatch's ids and names, with
 * the capacities the service read straight out of dispatch's own table merged
 * on by id. A vehicle the service knows nothing about keeps whatever the list
 * carried, which today is nothing.
 */
export function withVehicleCapacities(
  list: VehicleTypeItem[],
  capacities: BookingVehicleCapacity[],
): VehicleTypeItem[] {
  if (!capacities.length) return list;
  const byId = new Map(capacities.map((c) => [c.id, c]));
  return list.map((vehicle) => {
    const found = byId.get(vehicle.id);
    if (!found) return vehicle;
    return {
      ...vehicle,
      passengerCapacity: found.passengerCapacity ?? vehicle.passengerCapacity,
      luggageCapacity: found.luggageCapacity ?? vehicle.luggageCapacity,
    };
  });
}

/**
 * Pax and Lug from the chosen vehicle's capacity — the dashboard's
 * `applyVehicleCapacityPrefill`, in this form's string-valued state.
 *
 * Returns the fields to patch, empty when there is nothing to do, so a caller
 * inside `setForm` can spread it without deciding anything itself.
 *
 * Only a count nobody has typed is filled: a *string* is the unset test here
 * rather than a null, which is what keeps a typed "0" bags — a real answer —
 * from being overwritten by a vehicle that carries four. Off, or with no
 * capacity known, nothing is filled and both stay required.
 */
export function countsFromVehicle(
  form: Pick<BookingFormState, 'passengerCount' | 'luggageCount'>,
  vehicle: VehicleTypeItem | null | undefined,
  prefill: BookingPrefill = PREFILL_ON,
): Partial<Pick<BookingFormState, 'passengerCount' | 'luggageCount'>> {
  if (!prefill.paxLugFromVehicle || !vehicle) return {};
  const patch: Partial<Pick<BookingFormState, 'passengerCount' | 'luggageCount'>> = {};
  if (!form.passengerCount.trim() && typeof vehicle.passengerCapacity === 'number') {
    patch.passengerCount = String(vehicle.passengerCapacity);
  }
  if (!form.luggageCount.trim() && typeof vehicle.luggageCapacity === 'number') {
    patch.luggageCount = String(vehicle.luggageCapacity);
  }
  return patch;
}

/**
 * Preselect the fetched vehicle type that best matches the AI's free-text wish
 * ("black SUV" → "Luxury SUV"). Returns 0 when nothing overlaps — a wrong
 * preselection is worse than none.
 */
export function fuzzyVehicleMatch(
  preference: string | undefined,
  list: VehicleTypeItem[],
): number {
  const wish = tokens(stripVehicleLabel(preference ?? ''));
  if (!wish.length) return 0;
  let bestId = 0;
  let bestScore = 0;
  for (const vehicle of list) {
    if (vehicle.isActive === false) continue;
    const name = tokens(vehicle.name);
    const score = wish.filter((t) => name.includes(t)).length;
    if (score > bestScore) {
      bestScore = score;
      bestId = vehicle.id;
    }
  }
  return bestId;
}
